import { Card, CardHeader, CardContent } from '../../components/ui/card';
import { Save, LogOut } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useProfessionalViewModel } from '../../view-model/professional/professional-view-model';
import TimezoneSelect from '../timezone-select/timezone-select';
import { useTranslation } from 'react-i18next';
import { useAuthViewModel } from '../../view-model/auth-model-view';
import { useState } from 'react';
import LogoutConfirmationModal from '../ui/logout-confirmation-modal';
import ProfilePictureUpload from './profile-picture-upload';

const ProfilePersonal = observer(() => {
  const professionalViewModel = useProfessionalViewModel();
  const authViewModel = useAuthViewModel();
  const { t } = useTranslation();
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);

  return (
    <Card className="flex-1 overflow-hidden flex flex-col">
      <CardHeader>
        <h2 className="text-lg font-medium">{t('profile.personal', 'Profil personnel')}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('profile.personalDescription', 'Gérez vos informations personnelles')}
        </p>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">
        <form className="space-y-6"
              onSubmit={(e) => professionalViewModel.handleUpdateProfileProfessional(e)}>

          {/* Photo de profil */}
          <div className="flex justify-center py-6 border-b border-gray-200 dark:border-gray-700">
            <ProfilePictureUpload
              currentPictureUrl={professionalViewModel.picture}
              onUpload={professionalViewModel.uploadProfilePicture}
              isUploading={professionalViewModel.isUploadingPicture}
            />
          </div>

          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">{t('profile.firstName', 'Prénom')}</label>
              <input
                  disabled={!professionalViewModel.isUpdate}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                  placeholder="John"
                  onChange={e => professionalViewModel.setFirstName(e.target.value)}
                  value={professionalViewModel.firstName}
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">{t('profile.lastName', 'Nom')}</label>
              <input
                  disabled={!professionalViewModel.isUpdate}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                  placeholder="Smith"
                  onChange={e => professionalViewModel.setLastName(e.target.value)}
                  value={professionalViewModel.lastName}
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">{t('profile.email', 'Email')}</label>
              <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                  placeholder="john.smith@example.com"
                  value={professionalViewModel.email}
                  disabled
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">{t('profile.phone', 'Téléphone')}</label>
              <input
                  disabled={!professionalViewModel.isUpdate}
                  type="tel"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                  placeholder="+33 6 12 34 56 78"
                  onChange={e => professionalViewModel.setPhone(e.target.value)}
                  value={professionalViewModel.phone}
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">{t('profile.profession', 'Profession')}</label>
              <input
                  disabled={!professionalViewModel.isUpdate}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                  placeholder={t('profile.professionPlaceholder', 'Ex: Coiffeur') || ''}
                  onChange={e => professionalViewModel.setProfession(e.target.value)}
                  value={professionalViewModel.profession}
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1">
              {professionalViewModel.isUpdate ? (
                <TimezoneSelect />
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-1">{t('profile.timeZone', 'Fuseau horaire')}</label>
                  <input
                    disabled
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                    value={professionalViewModel.timeZone}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
                type="submit"
                className={`w-full flex items-center justify-center px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors ${
                    !professionalViewModel.isUpdate
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-primary hover:bg-primary-dark'
                }`}
            >
              <Save size={16} className="mr-2"/>
              {t('profile.updatePersonal', 'Enregistrer les changements')}
            </button>
          </div>
          
          {/* Bouton de déconnexion visible uniquement sur mobile */}
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 block md:hidden">
            <button
              type="button"
              onClick={() => setIsLogoutDialogOpen(true)}
              className="w-full flex items-center justify-center px-4 py-3 text-white bg-red-500 hover:bg-red-600 rounded-lg text-sm font-medium transition-colors"
            >
              <LogOut size={16} className="mr-2" />
              {t('sidebar.logout', 'Déconnexion')}
            </button>
          </div>
        </form>
      </CardContent>
      
      {/* Modal de confirmation de déconnexion */}
      <LogoutConfirmationModal
        isOpen={isLogoutDialogOpen}
        onClose={() => setIsLogoutDialogOpen(false)}
        onConfirm={() => authViewModel.logout()}
      />
    </Card>
  );
});

export default ProfilePersonal;
